import { useRef } from "react";
import { Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { Loader } from "../../../../components/common/loader";
import { getDocumentDisplayName } from "../../../../utils/onboarding";
import type { UserDtoDocumentsInnerTypeEnum } from "../../../../api/api";
import { appColors } from "../../../../common/constants/colors";
import { IconCircleCheck, IconCloudUpload, IconFileUpload, IconX } from "@tabler/icons-react";
interface DocumentUploadItemProps {
    type: UserDtoDocumentsInnerTypeEnum;
    isUploading: boolean;
    progress?: number;
    selectedFile: File | null;
    onFileSelect: (type: UserDtoDocumentsInnerTypeEnum, file: File | null) => void;
    onFileRemove: (type: UserDtoDocumentsInnerTypeEnum) => void;
    onConfirmUpload: (type: UserDtoDocumentsInnerTypeEnum) => void;
    hideUploadButton?: boolean;
    isUploaded?: boolean;
}
export function DocumentUploadItem({ type, isUploading, progress, selectedFile, onFileSelect, onFileRemove, onConfirmUpload, hideUploadButton = false, isUploaded = false, }: DocumentUploadItemProps) {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        onFileSelect(type, file);
        // Reset input so same file can be selected again
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };
    const handleSelectClick = () => {
        fileInputRef.current?.click();
    };
    const handleRemoveClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onFileRemove(type);
    };
    return (<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 border border-uiSecondary rounded-lg">
      <div className="flex items-center gap-3 flex-1">
        <IconCloudUpload size={24} style={{
            color: appColors.textPrimary
        }}/>
        <div className="flex-1">
          <Text variant="body" tone="dark">
            {getDocumentDisplayName(type)}
          </Text>
          {isUploaded ? (<div className="mt-1 flex items-center gap-1">
              <IconCircleCheck size={16} style={{
                color: appColors.green
            }}/>
              <Text variant="bodyXs">
                Uploaded: {selectedFile?.name || "Document"}
              </Text>
            </div>) : selectedFile ? (<Text variant="bodyXs" tone="body" className="mt-1">
              Selected: {selectedFile.name}
            </Text>) : (<Text variant="bodyXs" tone="body" className="mt-1">
              Please upload your {getDocumentDisplayName(type).toLowerCase()}
            </Text>)}

          {isUploading && progress != null && (<div className="mt-2">
              <Text variant="bodyXs" tone="body">
                Uploading {Math.round(progress * 100)}%
              </Text>
              <div className="mt-1 h-1.5 bg-uiSecondary rounded-full overflow-hidden">
                <div className="h-full bg-primary transition-all" style={{
                width: `${Math.min(Math.max(progress * 100, 0), 100)}%`,
            }}/>
              </div>
            </div>)}
        </div>
      </div>
      <div className="flex items-center gap-3 sm:flex-shrink-0">
        <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileChange} className="hidden" disabled={isUploading}/>

        {!selectedFile && !isUploaded ? (<Button variant="primary" onClick={handleSelectClick} disabled={isUploading} size="md" className="w-full sm:w-auto">
            {isUploading ? (<div className="flex items-center">
                <Loader inline size="sm" text=""/>
                <span className="ml-2">
                  {progress != null ? `Uploading ${progress}%` : "Uploading..."}
                </span>
              </div>) : (<div className="flex items-center">
                <IconFileUpload size={18}/>
                <span className="ml-2">Select</span>
              </div>)}
          </Button>) : (<>
            {!isUploaded && (<button onClick={handleRemoveClick} disabled={isUploading} className="p-2 hover:bg-uiSecondary rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed" aria-label="Remove selected file">
                <IconX size={20} style={{
                    color: appColors.textSecondary
                }}/>
              </button>)}
            {!hideUploadButton && !isUploaded && (<Button variant="primary" onClick={() => onConfirmUpload(type)} disabled={isUploading} size="md" className="w-full sm:w-auto">
                {isUploading ? (<div className="flex items-center">
                    <Loader inline size="sm" text=""/>
                    <span className="ml-2">
                      {progress != null
                        ? `Uploading ${progress}%`
                        : "Uploading..."}
                    </span>
                  </div>) : (<div className="flex items-center">
                    <IconFileUpload size={18}/>
                    <span className="ml-2">Upload</span>
                  </div>)}
              </Button>)}
          </>)}
      </div>
    </div>);
}
